/**
 * @file order-registry-query.ts
 * @description Состояние реестра заказов в адресе страницы (phase19.31, зона
 * «Заказы», макет (а)): поиск, «Мой ход / Все», стадия, источник, «только
 * открытые», страница и размер страницы.
 *
 * Всё живёт в адресе: ссылкой на свою очередь можно поделиться, F5 и «назад»
 * из карточки заказа возвращают тот же отбор и ту же страницу. Значения по
 * умолчанию в адрес не пишем — ссылка на «все заказы» остаётся короткой.
 *
 * Чистые функции без React: разбор, запись, сборка параметров запроса к
 * серверу (`offset/limit`), счётчик фильтров для кнопки «Фильтры · N».
 */

import type { listOrders, OrderSource } from "./api-orders";
import { positiveInt } from "./list-query";

/** Размеры страницы: 50 — по макету, 25/100 — на выбор. */
export const ORDERS_PAGE_SIZES = [25, 50, 100] as const;
export const ORDERS_DEFAULT_PAGE_SIZE = 50;

/** Чей ход: «Мой ход» — серверный фильтр `owner=me`. */
export type OrdersOwnerFilter = "me" | "all";

/** Источники заказа — список допустимых значений для разбора адреса. */
const ORDER_SOURCES: readonly OrderSource[] = ["CRM_MANUAL", "SITE", "TELEGRAM", "AVITO", "VK", "PHONE"];

export interface OrdersRegistryQuery {
  /** Номер заказа (или его часть). */
  search: string;
  owner: OrdersOwnerFilter;
  /** Стадия воронки заказов; пусто — все стадии. */
  stageId: string;
  source: "" | OrderSource;
  openOnly: boolean;
  /** Номер страницы с единицы. */
  page: number;
  pageSize: number;
}

/** Имена параметров адреса — одним реестром, а не строками по коду. */
const KEY = {
  search: "q",
  owner: "owner",
  stageId: "stage",
  source: "source",
  openOnly: "open",
  page: "page",
  pageSize: "size",
} as const;

/** Адрес → состояние реестра. Неизвестные значения тихо заменяются умолчаниями. */
export function parseOrdersRegistryQuery(params: URLSearchParams): OrdersRegistryQuery {
  const source = params.get(KEY.source) ?? "";
  const size = positiveInt(params.get(KEY.pageSize), ORDERS_DEFAULT_PAGE_SIZE);
  return {
    search: (params.get(KEY.search) ?? "").trim(),
    owner: params.get(KEY.owner) === "me" ? "me" : "all",
    stageId: params.get(KEY.stageId) ?? "",
    source: (ORDER_SOURCES as readonly string[]).includes(source) ? (source as OrderSource) : "",
    openOnly: params.get(KEY.openOnly) === "1",
    page: positiveInt(params.get(KEY.page), 1),
    pageSize: (ORDERS_PAGE_SIZES as readonly number[]).includes(size) ? size : ORDERS_DEFAULT_PAGE_SIZE,
  };
}

/** Состояние → адрес; значения по умолчанию не пишем. */
export function writeOrdersRegistryQuery(query: OrdersRegistryQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.search) params.set(KEY.search, query.search);
  if (query.owner === "me") params.set(KEY.owner, "me");
  if (query.stageId) params.set(KEY.stageId, query.stageId);
  if (query.source) params.set(KEY.source, query.source);
  if (query.openOnly) params.set(KEY.openOnly, "1");
  if (query.page > 1) params.set(KEY.page, String(query.page));
  if (query.pageSize !== ORDERS_DEFAULT_PAGE_SIZE) params.set(KEY.pageSize, String(query.pageSize));
  return params;
}

/**
 * Поменять часть состояния. Смена любого фильтра или размера страницы
 * возвращает на первую страницу: третья страница старого отбора в новом
 * отборе может не существовать.
 */
export function patchOrdersRegistryQuery(
  query: OrdersRegistryQuery,
  patch: Partial<OrdersRegistryQuery>,
): OrdersRegistryQuery {
  const keepPage = Object.keys(patch).every((key) => key === "page");
  return { ...query, ...patch, page: keepPage ? (patch.page ?? query.page) : 1 };
}

/** Параметры запроса к серверу: страница → `offset/limit`. */
export function toListOrdersParams(query: OrdersRegistryQuery): NonNullable<Parameters<typeof listOrders>[0]> {
  return {
    search: query.search || undefined,
    owner: query.owner === "me" ? "me" : undefined,
    stageId: query.stageId || undefined,
    source: query.source || undefined,
    openOnly: query.openOnly || undefined,
    limit: query.pageSize,
    offset: (query.page - 1) * query.pageSize,
  };
}

/**
 * Сколько фильтров спрятано в шторку на телефоне («Фильтры · N»): стадия,
 * источник, «только открытые». Поиск и «Мой ход» на телефоне видны сами.
 */
export function sheetFilterCount(query: OrdersRegistryQuery): number {
  return [query.stageId !== "", query.source !== "", query.openOnly].filter(Boolean).length;
}

/** Какой отбор включён — от этого зависит текст пустого списка. */
export type OrdersFilterKind = "none" | "owner" | "search" | "mixed";

export function ordersFilterKind(query: OrdersRegistryQuery): OrdersFilterKind {
  const owner = query.owner === "me";
  const search = query.search !== "";
  const others = sheetFilterCount(query) > 0;
  if (!owner && !search && !others) return "none";
  if (owner && !search && !others) return "owner";
  if (search && !owner && !others) return "search";
  return "mixed";
}

/** Сбросить все фильтры, сохранив размер страницы. */
export function resetOrdersRegistryFilters(query: OrdersRegistryQuery): OrdersRegistryQuery {
  return { search: "", owner: "all", stageId: "", source: "", openOnly: false, page: 1, pageSize: query.pageSize };
}

/** «1–50 из 312»: границы текущей страницы и число страниц. */
export function ordersPageRange(page: number, pageSize: number, total: number) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return { from, to, pages };
}
